import {AfterViewInit, Component, HostListener, inject, input, OnDestroy, ViewChild}                                                                                                                                                                                   from '@angular/core';
import {Timestamp}                                                                                                                                                                                                                                                     from '@angular/fire/firestore';
import {BodyRecordLogIntf, CaffRecordLogIntf, CrudRecordTypes, GlobalDataTypesDict, GlobalRecordIntf, GlobalRecordType, HkitRecordLogType, MedsRecordLogIntf, MindRecordLogIntf, MoodRecordLogIntf, PointWithData, TwColorUnionType, VapeRecordLogIntf, VapeTypesDict} from '@djsharkk/logr-types';
import {ChartConfiguration, ChartOptions, LinearScaleOptions, TimeScaleOptions, TooltipItem}                                                                                                                                                                           from 'chart.js';
import {AnnotationOptions, BoxLabelOptions}                                                                                                                                                                                                                            from 'chartjs-plugin-annotation';
import {DateTime}                                                                                                                                                                                                                                                      from 'luxon';
import {map, takeUntil}                                                                                                                                                                                                                                                from 'rxjs';
import {UiNumberFormatterService}                                                                                                                                                                                                                                      from '../../../logr-ui/utils/ui-number-formatter.service';
import {ChartScatterComponent}                                                                                                                                                                                                                                         from '../../comps/chart-scatter/chart-scatter.component';
import {ChartCatsBase}                                                                                                                                                                                                                                                 from '../chart-cats.base';


@Component({
			   selector   : 'app-chart-log-day',
			   imports    : [
				   ChartScatterComponent
			   ],
			   templateUrl: './chart-log-day.component.html',
			   styleUrl   : './chart-log-day.component.css'
		   })
export class ChartLogDayComponent extends ChartCatsBase<ChartScatterComponent, 'line' | 'scatter'> implements OnDestroy, AfterViewInit {
	@ViewChild('ChartWrapper') chartComponent!: ChartScatterComponent;
	svcUiNumberFormatter = inject(UiNumberFormatterService)

	date = input<Date>(new Date());

	@HostListener('document:visibilitychange', ['$event'])
	async onVisibilityChange() {
		if (document.visibilityState === 'visible') {
			this.svcChartProps.addNowLine(this.conf.options as ChartOptions);
		}
	}

	private hkitLogTypes: HkitRecordLogType[] = [
		'dietary_water',
		'handwashing',
		'toothbrushing'
	];

	private hkitLogTypesAudio: HkitRecordLogType[] = [
		'environmental_audio_exposure',
		'headphone_audio_exposure'
	]

	conf: ChartConfiguration<('scatter' | 'line'), PointWithData[]> = {
		data   : {
			datasets: [
				...this.svcChartDefaults.crudDatasets(),
				...this.svcChartDefaults.hkitDatasets(this.hkitLogTypes),
				...this.svcChartDefaults.hkitDatasets(this.hkitLogTypesAudio, 'yAudio').map(dataset => ({
					...dataset,
					pointStyle   : 'line',
					pointRotation: 90,
					pointRadius  : 1
				})),
				...this.svcChartDefaults.rizeDatasetsLine(),
				{
					label           : 'Heart Alerts',
					data            : [],
					pointStyle      : 'rect',
					pointRadius     : 1,
					pointBorderWidth: 0,
					backgroundColor : this.svcUiTheme.getTailwindColor('pink', 600),
					yAxisID         : 'yHeart'
				},
				{
					label           : 'Workout Heart',
					data            : [],
					pointStyle      : 'rect',
					pointRadius     : 1,
					pointBorderWidth: 0,
					backgroundColor : this.svcUiTheme.getTailwindColor('cyan', 600),
					yAxisID         : 'yHeart'
				},
				{
					label           : 'Heart',
					data            : [],
					pointStyle      : 'rect',
					pointRadius     : 1,
					pointBorderWidth: 0,
					backgroundColor : this.svcUiTheme.getTailwindColor('neutral', 700),
					yAxisID         : 'yHeart'
				}
			],
			labels  : []
		},
		options: this.svcChartProps.optsHour,
		plugins: [],
		type   : 'scatter'
	};


	dateMin
	dateMax

	constructor() {
		super();

		this.dateMin = DateTime.fromJSDate(this.date()).startOf('day')
		this.dateMax = DateTime.fromJSDate(this.date()).endOf('day')

		const tooltipConfig = {
			...this.conf.options?.plugins?.tooltip
		};

		if (this.conf.options?.plugins?.tooltip) {
			this.conf.options.plugins.tooltip = {
				...tooltipConfig,
				intersect: true,
				mode     : 'nearest',
				callbacks: {
					label: (tooltipItem: TooltipItem<'scatter'>): string | string[] | void => {
						const dataset = tooltipItem.datasetIndex;
						const dataValue = tooltipItem.raw;
						const datapoint = tooltipItem.dataset.data[tooltipItem.dataIndex] as PointWithData;
						const timestamp = DateTime.fromMillis(datapoint.x).toFormat('HH:mm')
						if (datapoint) {
							if (dataset == 0) {
								const d = datapoint.d as VapeRecordLogIntf
								return `${timestamp}: ${d.qtyMg.toFixed()}mg ${VapeTypesDict[d.type].name} ${(d.strength * 100).toFixed()}%`.toUpperCase();
							} else if (dataset === 1) {
								const d = datapoint.d as MedsRecordLogIntf
								return `${timestamp}: ${d.dosage}${d.unit} ${this.svcDataFactory.svcDataMeds.getMedById(d.medication)?.medication.slice(0, 4)}`.toUpperCase();
							} else if (dataset === 2) {
								const d = datapoint.d as MoodRecordLogIntf
								return `${timestamp}: ${d.label}`.toUpperCase();
							} else if (dataset === 3) {
								const d = datapoint.d as MindRecordLogIntf
								return `${timestamp}: ${d.mind.join(', ')}`.toUpperCase();
							} else if (dataset === 4) {
								const d = datapoint.d as BodyRecordLogIntf
								return `${timestamp}: ${d.body.join(', ')}`.toUpperCase();
							} else if (dataset === 5) {
								const d = datapoint.d as CaffRecordLogIntf
								return `${timestamp}: ${d.mg}mg Caffeine`.toUpperCase();
							} else {
								return `${timestamp}: ${GlobalDataTypesDict[datapoint.t].name}`.toUpperCase();
							}
						} else {
							return `${dataset}: ${dataValue}`;
						}

					}

				}
			}
		}

		const defaultScales = {
			...this.conf.options?.scales
		};

		const defaultY: LinearScaleOptions = {
			...defaultScales?.['y'] as LinearScaleOptions
		};
		const defaultX: TimeScaleOptions = {
			...defaultScales?.['x'] as TimeScaleOptions
		};


		if (this.conf.options?.scales) {
			this.conf.options.scales = {
				...defaultScales,
				x     : {
					...defaultX,
					min   : this.dateMin.toMillis(),
					max   : this.dateMax.toMillis(),
					offset: false
				} as TimeScaleOptions,
				y     : {
					...defaultY,
					offset  : false,
					position: 'center',
					min     : 0,
					max     : 8,
					ticks   : {
						...defaultY?.ticks,
						padding : 0,
						stepSize: 1,
						display : false,
						mirror  : false,
						callback: (tickValue) => tickValue.toString().padStart(7, '\u00A0')

					}
				} as LinearScaleOptions,
				yHeart: {
					...defaultY,
					offset      : false,
					position    : 'center',
					min         : 0,
					suggestedMax: 160,
					ticks       : {
						...defaultY?.ticks,
						display : false,
						padding : 0,
						align   : 'inner',
						stepSize: 20,
						mirror  : false,
						callback: (tickValue) => tickValue as number > 210 ? '' : tickValue.toString().padStart(7, '\u00A0')
					},
					grid        : {
						...defaultY?.grid,
						display   : false,
						tickLength: 0
					}
				} as LinearScaleOptions,
				yAudio: {
					...defaultY,
					offset  : false,
					position: 'center',
					min     : 0,
					ticks   : {
						...defaultY?.ticks,
						display: false
					},
					grid    : {
						...defaultY?.grid,
						display   : false,
						tickLength: 0
					}
				} as LinearScaleOptions
			}
		}


	}

	ngAfterViewInit() {

		const getPosY = (lists: any[][]): number => lists.map(list => (list.length / 4)).reduce((a, b) => a + b, 0) + 1;

		CrudRecordTypes.forEach((type, index) => {
			this.svcDataFactory.crudDataSvcs[type]
				.getRecordsLogDates([this.date()])
				.pipe(takeUntil(this.dataTrash))
				.subscribe(records => {
					this.chartComponent?.updateDataset(
						index,
						{
							data: this.plotDayRecords(records, 1 + (index / 4), undefined, type)
						}
					)
				})
		})

		this.svcDataFactory.svcDataHkit
			.getRecordsLogDates([this.date()])
			.pipe(takeUntil(this.dataTrash))
			.subscribe(records => {

				this.hkitLogTypes.forEach((type, index) => {
					const recs = this.svcDataFactory.svcDataHkit.getRecordsByType(records, type)
					const data = this.plotDayRecords(recs, getPosY([CrudRecordTypes]) + (index / 4), 'timestamp', type)
					this.chartComponent?.updateDataset(
						(CrudRecordTypes.length) + index,
						{
							data
						}
					)
				})

				this.hkitLogTypesAudio.forEach((type, index) => {
					const recs = this.svcDataFactory.svcDataHkit.getRecordsByType(records, type)
					const data = this.plotDayRecords(recs, (d) => d.qty, 'timestamp', type)
					this.chartComponent?.updateDataset(
						(CrudRecordTypes.length + this.hkitLogTypes.length) + index,
						{
							data
						}
					)
				})

			})

		const startingInt = this.hkitLogTypes.length + this.hkitLogTypesAudio.length + CrudRecordTypes.length;

		this.svcDataFactory.svcDataRize.rizeSessions
			.pipe(takeUntil(this.dataTrash))
			.pipe(map(records => records.filter(record => DateTime.fromJSDate(this.date()).hasSame(DateTime.fromJSDate(record.startTime), 'day'))))
			.subscribe(records => {
				this.chartComponent?.updateDataset(
					startingInt,
					{
						data: this.svcChartDefaults.rizeDataPoints('focus', records)
					}
				)

				this.chartComponent?.updateDataset(
					startingInt + 1,
					{
						data: this.svcChartDefaults.rizeDataPoints('break', records)
					}
				)

				this.chartComponent?.updateDataset(
					startingInt + 2,
					{
						data: this.svcChartDefaults.rizeDataPoints('meeting', records)
					}
				)

			})

		this.svcDataFactory.svcDataHkit.recordsHeartAlerts
			.pipe(takeUntil(this.dataTrash))
			.pipe(map(records => records.filter(record => DateTime.fromJSDate(this.date()).hasSame(DateTime.fromMillis(record.start.toMillis()), 'day'))))
			.subscribe(records => {
				this.chartComponent?.updateDataset(
					startingInt + 3,
					{
						data: records
							.map(record => record.heartRateData.map(hrd => {
								return {
									x: this.svcChartDefaults.equaliseX(DateTime.fromFormat(hrd.timestamp.start, "yyyy-MM-dd'T'HH:mm:ss'Z'", {zone: 'utc'}).toJSDate(), this.date()),
									y: hrd.heartRate
								}
							})).flat()
					}
				)
			})

		this.svcDataFactory.svcDataHkit.recordsWorkouts
			.pipe(takeUntil(this.dataTrash))
			.pipe(map(records => records.filter(record => DateTime.fromJSDate(this.date()).hasSame(DateTime.fromMillis(record.start.toMillis()), 'day'))))
			.subscribe(records => {


				const heartData = records.map(record => {
					return record.heartRateData.map(hrd => {
						return {
							x: this.svcChartDefaults.equaliseX(DateTime.fromFormat(hrd.date, 'yyyy-MM-dd HH:mm:ss ZZZ').toJSDate(), this.date()),
							y: hrd.Avg
						}
					})
				}).flat()

				records.forEach(record => {
					this.buildAnnotation(
						{
							type           : 'box',
							xMin           : record.start.toMillis(),
							xMax           : record.end.toMillis(),
							backgroundColor: this.svcChartProps.stripeAnnotationStylesWeekdays.workout,
							borderWidth    : 0,
							z              : -1
						},
						DateTime.fromMillis(record.start.toMillis()).toISO() || 'No Date',
						this.conf.options as ChartOptions,
						'workout'
					)
				})


				this.chartComponent?.updateDataset(
					startingInt + 4,
					{
						data: heartData
					}
				)


			})

		this.svcDataFactory.svcDataOura.recordsOuraHeart
			.pipe(takeUntil(this.dataTrash))
			.pipe(map(records => records.filter(record => DateTime.fromJSDate(this.date()).hasSame(DateTime.fromJSDate(record.timestamp), 'day'))))
			.subscribe(records => {

				const yMax = Math.max(0, Math.max(...records.map(r => r.bpm)));
				const yMin = Math.max(0, Math.min(...records.map(r => r.bpm)));

				if (records.length > 0) {

					const anno = (n: number): AnnotationOptions => {
						return {
							yScaleID   : 'yHeart',
							type       : 'line',
							borderWidth: 1,
							borderColor: this.svcUiTheme.getTailwindColor('neutral', 600),
							borderDash : [
								2,
								2
							],
							label      : {
								display        : true,
								content        : `${n}BPM`,
								position       : 'end',
								color          : this.svcUiTheme.getTailwindColor('neutral', 600),
								textStrokeWidth: 3,
								textStrokeColor: this.svcUiTheme.getTailwindColor('neutral', 950),
								backgroundColor: 'transparent',
								padding        : 0,
								font           : {
									family: 'decima_mono_roundregular',
									size  : 9,
									weight: 'normal'
								}
							}
						}
					}

					this.svcChartProps.buildAnnotation(
						{
							...anno(yMax),
							yMin: yMax,
							yMax: yMax
						},
						`heart-max`,
						this.conf.options as ChartOptions
					)

					this.svcChartProps.buildAnnotation(
						{
							...anno(yMin),
							yMin: yMin,
							yMax: yMin
						},
						`heart-min`,
						this.conf.options as ChartOptions
					)
				}

				this.chartComponent?.updateDataset(
					startingInt + 5,
					{
						data: this.plotDayRecords(records, (d) => d.bpm, 'timestamp', 'heart')
					}
				)
			})


		this.svcDataFactory.svcDataOura.recordsOuraSleep
			.pipe(takeUntil(this.dataTrash))
			.pipe(map(records => records.filter(record => DateTime.fromJSDate(this.date()).hasSame(DateTime.fromJSDate(record.day), 'day'))))
			.subscribe(records => {
				records.forEach(record => {

					const labels = [
						(`${this.svcUiNumberFormatter.minsToClock(record.total_sleep_duration / 60)}h`),
						(`${DateTime.fromJSDate(record.bedtime_start).toFormat('HH:mm')} - ${DateTime.fromJSDate(record.bedtime_end).toFormat('HH:mm')}`),
						`${record.efficiency}% ${record.readiness.temperature_trend_deviation}ºC`
					]

					const maxLines = Math.max(...labels.map(label => label.length))

					this.buildAnnotation(
						{
							type           : 'box',
							xMin           : record.adjustedBedtimeStart.getTime() || record.bedtime_start.getTime(),
							xMax           : record.bedtime_end.getTime(),
							backgroundColor: this.svcChartProps.stripeAnnotationStylesWeekdays.sleep,
							borderWidth    : 0,
							label          : this.buildAnnotationLabel(
								labels.map(label => label.padEnd(maxLines, '\u00A0')),
								'neutral',
								record.type !== 'long_sleep'
							),
							z              : -1
						},
						DateTime.fromJSDate(record.adjustedBedtimeStart || record.bedtime_start).toISO() || 'No Date',
						this.conf.options as ChartOptions,
						'sleep'
					)
				})
			});


		this.svcChartProps.addNowLine(this.conf.options as ChartOptions);
	}


	buildAnnotationLabel(content: string[], color: TwColorUnionType, offset = true): BoxLabelOptions {
		return {
			display  : true,
			content  : content.map(c => c.padEnd(24, '\u00A0')),
			font     : {
				family: 'decima_mono_roundregular',
				size  : 9,
				weight: 'normal'
			},
			color    : this.svcUiTheme.getTailwindColor(color, 600),
			position : {
				x: 'start',
				y: 'start'
			},
			textAlign: 'start',
			yAdjust  : 4,
			xAdjust  : 4,
			padding  : 0
			// rotation : 90
		}
	}

	buildAnnotation(annotation: AnnotationOptions, keyName: string, opts: ChartOptions, type: GlobalRecordType): void {
		const annotationPlugin = opts?.plugins?.annotation;
		if (!annotationPlugin) {
			return;
		}

		const existingAnnotations = (annotationPlugin.annotations as Record<string, AnnotationOptions>) || {};
		annotationPlugin.annotations = {
			...existingAnnotations,
			[`${type}${keyName}`]: {
				...annotation,
				drawTime: 'beforeDatasetsDraw'
			}
		};
	}


	plotDayRecords<D = GlobalRecordIntf, T = GlobalRecordType>(
		data: D[],
		y: number | ((d: D) => number),
		dateKey: keyof D = 'timestamp' as keyof D,
		type: T
	): PointWithData[] {


		return data.map(record => {
			let objDate: DateTime;

			if (record[dateKey] instanceof Timestamp) {
				objDate = DateTime.fromMillis(record[dateKey].toMillis());
			} else if (record[dateKey] instanceof Date) {
				objDate = DateTime.fromJSDate(record[dateKey]);
			} else {
				return {
					x: NaN,
					y: NaN,
					d: false,
					t: type as GlobalRecordType
				};
			}

			return {
				x: this.svcChartDefaults.equaliseX(objDate.toJSDate(), this.date()),
				y: typeof y === 'function' ? y(record) : y,
				d: record as GlobalRecordIntf,
				t: type as GlobalRecordType
			}
		})
	}
}
